// Compare/Review: each project database's Index Sheets and manufacturers that are not in the
// master catalog, differ from it, or (sheets) do not follow the code grammar, with promotion
// into the catalog and a difference viewer for datasheets.
import { useState, useEffect, useCallback, Fragment } from 'react';
import { RefreshCw, Upload, Eye } from 'lucide-react';
import { dsMasterCatalogApi, dsComposerApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { dsErrMsg, linesToBoxes, toIndexSide } from './dsBoxes';
import { sheetLayout } from '../development/dsTemplateLayout';
import { useDsTemplates } from '../development/useDsTemplates';
import DsDiffViewerModal from './DsDiffViewerModal';
import { PromoteModal, ConfirmModal } from './CatalogModals';
import { differenceSummary, hasDatasheetDiff, alignRevision } from './masterCatalog';
import StuiSelect from '../../components/common/StuiSelect';

const ALL = '';

// One tab per kind of finding, so each list reads on its own instead of one long scroll.
const GROUPS = [
  { id: 'notInCatalog',  label: 'Not in catalog', pick: res => res.sheets?.notInCatalog },
  { id: 'differs',       label: 'Differs',        pick: res => res.sheets?.differs },
  { id: 'nonConforming', label: 'Non-conforming', pick: res => res.sheets?.nonConforming },
  { id: 'manufacturers', label: 'Manufacturers',  pick: res => res.manufacturers?.notInCatalog, pick2: res => res.manufacturers?.differs },
];

const revLabel = key => (key ? key.slice(key.lastIndexOf('_') + 1) : '');

export default function CatalogCompareSection({ nav = null, allDsIndex = [], statuses = [], onCatalogChanged }) {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const auditCfg = { headers: { 'X-Changed-By': currentUser?.email || '' } };
  const specRows = useDsTemplates();

  const [databases, setDatabases] = useState([]);
  const [database, setDatabase] = useState(ALL);
  const [results, setResults] = useState(null);
  const [group, setGroup] = useState(GROUPS[0].id);
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(null);   // { database, row }
  const [promoteMfr, setPromoteMfr] = useState(null); // { database, row }
  const [busy, setBusy] = useState(false);
  const [diff, setDiff] = useState(null);             // props for DsDiffViewerModal + its source

  useEffect(() => {
    dsMasterCatalogApi.getDatabases()
      .then(r => setDatabases(r.data ?? []))
      .catch(e => notify.error(dsErrMsg(e, 'Failed to load databases.')));
  }, [notify]);

  const displayName = name => databases.find(d => d.databaseName === name)?.displayName || name;

  const runCompare = useCallback(async (db = database) => {
    setLoading(true);
    try {
      const res = await dsMasterCatalogApi.compare(db || undefined);
      setResults(res.data ?? []);
    } catch (e) { notify.error(dsErrMsg(e, 'Compare failed.')); }
    finally { setLoading(false); }
  }, [database, notify]);

  // Re-reads one database's result after a promotion, leaving the others as they are.
  const refreshOne = async db => {
    try {
      const res = await dsMasterCatalogApi.compare(db);
      const fresh = (res.data ?? [])[0];
      setResults(prev => (prev ?? []).map(r => (r.database === db && fresh ? fresh : r)));
    } catch (e) { notify.error(dsErrMsg(e, 'Compare failed.')); }
  };

  const handlePromote = async status => {
    const { database: db, row } = promoting;
    setBusy(true);
    try {
      const res = await dsMasterCatalogApi.promote({ database: db, indexSheetId: row.id, status }, auditCfg);
      const d = res.data;
      const vals = d.datasheet?.values;
      notify.success(`${d.indexSheet} ${d.sheetAction} in the catalog as ${d.status}` +
        (vals ? ` (values: ${vals.inserted} added, ${vals.updated} changed, ${vals.deleted} removed)` : '') + '.');
      (d.warnings ?? []).forEach(w => notify.warning(w));
      setPromoting(null);
      setDiff(null);
      onCatalogChanged?.();
      await refreshOne(db);
    } catch (e) { notify.error(dsErrMsg(e, 'Promote failed.')); }
    finally { setBusy(false); }
  };

  const handlePromoteMfr = async () => {
    const { database: db, row } = promoteMfr;
    setBusy(true);
    try {
      const res = await dsMasterCatalogApi.promoteManufacturer({ database: db, manufacturerId: row.id }, auditCfg);
      notify.success(`Manufacturer ${res.data.indexCode} ${res.data.action} in the catalog.`);
      setPromoteMfr(null);
      onCatalogChanged?.();
      await refreshOne(db);
    } catch (e) { notify.error(dsErrMsg(e, 'Promote failed.')); }
    finally { setBusy(false); }
  };

  // Loads both sides of one sheet's datasheet and opens the difference viewer: the catalog's
  // current revision on the left, the project's on the right.
  const openDiff = async (db, row) => {
    try {
      const { data } = await dsMasterCatalogApi.compareSheet(db, row.id);
      const p = data.project;
      const c = data.catalog;
      const [pLines, cLines] = await Promise.all([
        dsComposerApi.getComposerLines(p.dsConfig),
        c?.dsConfig && c.dsConfig !== p.dsConfig ? dsComposerApi.getComposerLines(c.dsConfig) : Promise.resolve(null),
      ]);
      const pBoxes = linesToBoxes(pLines.data ?? []).map(toIndexSide);
      const cBoxes = c?.dsConfig
        ? (cLines ? linesToBoxes(cLines.data ?? []).map(toIndexSide) : pBoxes)
        : [];
      const aligned = alignRevision(cBoxes, pBoxes, p.values);
      const rec = allDsIndex.find(d => d.id === p.dsConfig);
      const sheetCount = Math.max(1, rec?.sheet_Count ?? 1);
      setDiff({
        database: db,
        row,
        title: `${row.indexSheet}: catalog vs ${displayName(db)}`,
        leftLabel: c?.dsIndex ? `Catalog (rev ${revLabel(c.dsIndex)})` : 'Catalog (none)',
        leftEmptyNote: "The catalog has no datasheet for this sheet; promoting brings this database's.",
        rightLabel: `${displayName(db)} (rev ${revLabel(p.dsIndex)})`,
        leftBoxes: cBoxes,
        rightBoxes: aligned.boxes,
        leftValues: c?.values ?? {},
        rightValues: aligned.values,
        sheetCount,
        layout: sheetLayout(specRows, rec?.ds_TempType ?? null, 1, sheetCount, rec?.ds_Name ?? null),
      });
    } catch (e) { notify.error(dsErrMsg(e, 'Failed to load the datasheets.')); }
  };

  // One group across every compared database: the database column only earns its place when
  // more than one database was compared.
  const groupRows = (pick) => (results ?? []).filter(res => !res.error)
    .flatMap(res => (pick(res) ?? []).map(r => ({ db: res.database, r })));
  const multiDb = (results ?? []).length > 1;
  const groupCount = id => {
    const g = GROUPS.find(x => x.id === id);
    return groupRows(g.pick).length + (g.pick2 ? groupRows(g.pick2).length : 0);
  };

  const sheetRows = (kind, rows) => (
    <Fragment key={kind}>
      {rows.map(({ db, r }) => (
        <tr key={`${kind}-${db}-${r.id}`}>
          {multiDb && <td>{displayName(db)}</td>}
          <td className="stui-mono">{r.indexSheet}</td>
          <td>{r.description}</td>
          <td>{r.manufacturerCode}</td>
          <td>{kind === 'nonConforming' ? <span className="stui-td-muted">Rename to the code grammar first</span> : differenceSummary(r) || (kind === 'notInCatalog' ? 'Not in catalog' : '')}</td>
          <td className="stui-mono">{r.dsIndex}{r.valueCount ? ` (${r.valueCount})` : ''}</td>
          <td>
            <div className="stui-row-4">
              {hasDatasheetDiff(r) && kind === 'differs' && (
                <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => openDiff(db, r)} title="Compare datasheets">
                  <Eye size={14} />
                </button>
              )}
              {r.promotable
                ? <button className="stui-btn stui-btn-primary stui-btn-sm" onClick={() => setPromoting({ database: db, row: r })}><Upload size={14} /> Promote</button>
                : kind !== 'nonConforming' && <span className="stui-td-muted" title={r.blocker}>Held</span>}
            </div>
          </td>
        </tr>
      ))}
    </Fragment>
  );

  const mfrRows = (kind, label, rows) => rows.length === 0 ? null : (
    <Fragment key={kind}>
      <tr className="stui-data-grid-section-row"><th colSpan={multiDb ? 5 : 4}>{label} ({rows.length})</th></tr>
      {rows.map(({ db, r }) => (
        <tr key={`${kind}-${db}-${r.id}`}>
          {multiDb && <td>{displayName(db)}</td>}
          <td className="stui-mono">{r.indexCode}</td>
          <td>{r.manufacturer}</td>
          <td>{r.held ? <span className="stui-td-muted">{r.blocker}</span> : (r.fieldDiffs ?? []).map(d => d.field).join(', ') || 'Not in catalog'}</td>
          <td>
            {r.promotable && (
              <button className="stui-btn stui-btn-primary stui-btn-sm" onClick={() => setPromoteMfr({ database: db, row: r })}><Upload size={14} /> Promote</button>
            )}
          </td>
        </tr>
      ))}
    </Fragment>
  );

  return (
    <>
      <div className="stui-page-header">
        <div className="stui-row-8">
          {nav}
          <label className="stui-label">Database</label>
          <StuiSelect className="stui-select" value={database} onChange={e => setDatabase(e.target.value)} disabled={loading}>
            <option value={ALL}>All databases</option>
            {databases.map(d => <option key={d.databaseName} value={d.databaseName}>{d.displayName || d.databaseName}</option>)}
          </StuiSelect>
          <button className="stui-btn stui-btn-primary" onClick={() => runCompare()} disabled={loading}>
            <RefreshCw size={14} /> {loading ? 'Comparing…' : 'Compare'}
          </button>
        </div>
        <span className="stui-text-sm-muted">Placeholders (N/A, Integral To…, See Panels, TBD, blank) are not listed.</span>
      </div>

      <div className="stui-detail-fill stui-mt-sm">
        {results === null ? (
          <div className="stui-detail-empty">Pick a database, or all of them, and press Compare.</div>
        ) : results.length === 0 ? (
          <div className="stui-detail-empty">No databases to compare.</div>
        ) : (
          <>
            <div className="stui-module-header-tabs">
              {GROUPS.map(g => (
                <button key={g.id} className={`stui-dataentry-tab${group === g.id ? ' active' : ''}`} onClick={() => setGroup(g.id)}>
                  <span>{g.label} ({groupCount(g.id)})</span>
                </button>
              ))}
            </div>
            {results.filter(res => res.error).map(res => (
              <div key={res.database} className="stui-inline-notice stui-inline-notice-error stui-mt-sm">
                {displayName(res.database)} could not be read: {res.error}
              </div>
            ))}
            <div className="stui-data-grid-container">
              <div className="stui-data-grid-scroll-area">
                {(() => {
                  const g = GROUPS.find(x => x.id === group);
                  if (groupCount(g.id) === 0) return <div className="stui-detail-empty">Nothing in this group.</div>;
                  if (g.id === 'manufacturers') return (
                    <table className="stui-data-grid stui-data-grid-auto">
                      <thead>
                        <tr>{multiDb && <th>Database</th>}<th>Mfr Code</th><th>Manufacturer</th><th>Differences</th><th /></tr>
                      </thead>
                      <tbody>
                        {mfrRows('mNotIn', 'Not in catalog', groupRows(g.pick))}
                        {mfrRows('mDiffers', 'Differ from catalog', groupRows(g.pick2))}
                      </tbody>
                    </table>
                  );
                  return (
                    <table className="stui-data-grid stui-data-grid-auto">
                      <thead>
                        <tr>{multiDb && <th>Database</th>}<th>Index Sheet</th><th>Description</th><th>Mfr</th><th>Differences</th><th>Datasheet (values)</th><th /></tr>
                      </thead>
                      <tbody>{sheetRows(g.id, groupRows(g.pick))}</tbody>
                    </table>
                  );
                })()}
              </div>
            </div>
          </>
        )}
      </div>

      {diff && (
        <DsDiffViewerModal
          {...diff}
          onClose={() => setDiff(null)}
          actions={diff.row.promotable ? [{ label: 'Promote this version', primary: true, onClick: () => setPromoting({ database: diff.database, row: diff.row }) }] : []}
        />
      )}
      {promoting && (
        <PromoteModal database={displayName(promoting.database)} row={promoting.row} statuses={statuses}
          busy={busy} onPromote={handlePromote} onClose={() => setPromoting(null)} />
      )}
      {promoteMfr && (
        <ConfirmModal title={`Promote manufacturer ${promoteMfr.row.indexCode}`} confirmLabel="Promote" busy={busy}
          onConfirm={handlePromoteMfr} onClose={() => setPromoteMfr(null)}>
          <p className="stui-hint">
            {promoteMfr.row.catalogId != null
              ? `The catalog's ${promoteMfr.row.indexCode} is refreshed from ${displayName(promoteMfr.database)}: ${(promoteMfr.row.fieldDiffs ?? []).map(d => d.field).join(', ')}.`
              : `${promoteMfr.row.manufacturer} (${promoteMfr.row.indexCode}) is added to the catalog from ${displayName(promoteMfr.database)}.`}
          </p>
        </ConfirmModal>
      )}
    </>
  );
}
